import {Component, OnInit, OnDestroy} from '@angular/core';
import {Sidenavaction} from 'src/assets/js/application/sidenavaction';
import {DomSanitizer} from '@angular/platform-browser';
import {BusinessEntityService} from 'src/app/services/businessEntity.service';
import {FarmSiteService} from 'src/app/services/farmSite.service';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import {ReadingsService} from 'src/app/services/readings.service';
import {SpeciesTypeService} from 'src/app/services/speciesType.service';
import {PondCultureService} from 'src/app/services/pondCulture.service';
import {PondSectionService} from 'src/app/services/pondSection.service';
import {PersonsService} from 'src/app/services/persons.service';
import {FeedLogService} from 'src/app/services/feedLog.service';
import {TreatmentLogService} from 'src/app/services/treatmentLog.service';
import {SamplingService} from 'src/app/services/sampling.service';
import {HarvestLogService} from 'src/app/services/harvestLog.service';
import {ObservationLogService} from 'src/app/services/observationLog.service';
import LandingPageModel from 'src/app/services/landing-page.model';
import {ImageDetailsService} from 'src/app/services/images-details.service';
import ImageDetailsModel from 'src/app/services/images-details.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import {ToastrService} from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { Roles } from 'src/app/common/permissionModel';
import { Router } from '@angular/router';

declare var $: any;

/**
 * @component BlubayDashboardComponent
 * @selector app-blubay-dashboard
 * @extends LandingPageModel
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `BlubayDashboardComponent` serves as the central dashboard for the BluBay application.
 * It aggregates and displays critical farm operational data, including feed logs, treatment logs,
 * sampling data, harvest logs, and observation logs. It also provides summaries of pond status
 * (active/inactive), culture details, and device connectivity.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches and aggregates data from multiple services (Feed, Treatment, Sampling, etc.) using `Promise.all` for performance.
 * 2. **Role-Based Data Loading**: Adapts data retrieval strategies based on the logged-in user's role:
 *    - **Farmer/Business Admin (Farm)**: Loads data for all farm sites owned by the business.
 *    - **Farm Manager**: Loads data for farm sites assigned to the manager.
 *    - **Business Admin (Provider)**: Loads data for farm sites associated with the provider.
 *    - **Admin**: Loads system-wide data (fallback).
 * 3. **Dashboard Metrics**: Calculates and displays key metrics such as total active ponds, total stocking count, total area, and water quality test counts.
 * 4. **Interactive Elements**:
 *    - **Pond Selection**: Allows users to select specific ponds to view detailed metrics and images.
 *    - **Chat/Communication**: Integrated chat functionality (via `socketService`) for communication between stakeholders.
 *    - **Image Gallery**: Displays images associated with ponds/sites.
 * 5. **Pagination**: Manages pagination for various log tables displayed on the dashboard.
 *
 * ## User Roles & Permissions:
 * - **Farmer / Business Admin (Farm)**: Full access to their farm's operational data.
 * - **Farm Manager**: Access limited to assigned farm sites.
 * - **Business Admin (Provider)**: Access to client farms' data for monitoring.
 * - **Admin**: System-wide access (typically for support/debugging).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context (Farm/Provider).
 * - `businessID`: Logged-in business ID.
 * - `personID`: Logged-in user ID.
 * - `loginBusinessName`: Display name.
 *
 * @service BusinessEntityService - For business details.
 * @service FarmSiteService - For farm site hierarchy.
 * @service PondCultureService - For culture data.
 * @service ReadingsService - For water quality readings.
 * @service FeedLogService - For feed data.
 * @service TreatmentLogService - For treatment data.
 * @service SamplingService - For sampling data.
 * @service HarvestLogService - For harvest data.
 * @service ObservationLogService - For observation data.
 * @service ImageDetailsService - For image handling.
 * @service CustomerPondService - For pond management.
 * @service CommonMethodsService - For utility functions.
 * @service SMEChatService - For real-time chat (via socketService).
 */
@Component({
  selector: 'app-blubay-dashboard',
  templateUrl: './blubay-dashboard.component.html',
  styleUrls: ['./blubay-dashboard.component.scss']
})
export class BlubayDashboardComponent extends LandingPageModel implements OnInit, OnDestroy{
  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Business category of the logged-in user. */
  public loginBusinessCategory: string;

  /** @type {string} ID of the logged-in business. */
  public businessId: string;

  /** @type {string} ID of the logged-in person. */
  public personId: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {boolean} Flag for response loading state. */
  public responseLoader: boolean;

  /** @type {boolean} Flag for page loading state. */
  public pgLoad = false;

  /** @type {boolean} Flag to show/hide image modal. */
  public imageshow: boolean;

  /** @type {any} Currently selected image object. */
  public selectedImageObj: any;

  // pagination sorting
  /** @type {number} Current page number for general lists. Default 1. */
  public currentPageNumber: number = 1;

  /** @type {number} Items per page for general lists. Default 15. */
  public noOfItemsPerPage: number = 15;

  /*meal pagination*/
  /** @type {number} Current page number for meal logs. */
  public mealCurrentPageNumber: number = 1;

  /** @type {number} Items per page for meal logs. */
  public mealNoOfItemsPerPage: number = 15;
  /*meal pagination end*/

  /*treatment pagination*/
  /** @type {number} Current page number for treatment logs. */
  public currentTreatmentPageNumber: number = 1;

  /** @type {number} Items per page for treatment logs. */
  public noOfTreatmentItemsPerPage: number = 15;
  /*treatment pagination end*/

  /*Observation pagination*/
  /** @type {number} Current page number for observation logs. */
  public currentObservationPageNumber: number = 1;

  /** @type {number} Items per page for observation logs. */
  public noOfObservationItemsPerPage: number = 15;
  /*Observation pagination end*/

  /*Sampling pagination*/
  /** @type {number} Current page number for sampling logs. */
  public currentSamplingPageNumber: number = 1;

  /** @type {number} Items per page for sampling logs. */
  public noOfSamplingItemsPerPage: number = 15;
  /*Sampling pagination end*/

  /*sr pagination*/
  /** @type {number} Current page number for service requests. */
  public currentSrPageNumber: number = 1;

  /** @type {number} Items per page for service requests. */
  public noOfSrItemsPerPage: number = 15;
  /*sr pagination end*/

  /*Harvest pagination*/
  /** @type {number} Current page number for harvest logs. */
  public currentHarvestPageNumber: number = 1;

  /** @type {number} Items per page for harvest logs. */
  public noOfHarvestItemsPerPage: number = 15;
  /*Harvest pagination end*/

  /*Culture pagination*/
  /** @type {number} Current page number for culture lists. */
  public currentCulturePageNumber: number = 1;

  /** @type {number} Items per page for culture lists. */
  public noOfCultureItemsPerPage: number = 15;
  /*Culture pagination end*/

  /*Provider pagination*/
  /** @type {number} Current page number for provider lists. */
  public currentProviderPageNumber: number = 1;

  /** @type {number} Items per page for provider lists. */
  public noOfProviderItemsPerPage: number = 15;
  /*Provider pagination end*/

  /*Farm pagination*/
  /** @type {number} Current page number for farm lists. */
  public currentFarmPageNumber: number = 1;

  /** @type {number} Items per page for farm lists. */
  public noOfFarmItemsPerPage: number = 15;
  /*Farm pagination end*/

  /*Tanks pagination*/
  /** @type {number} Current page number for tank lists. */
  public currentTanksPageNumber: number = 1;

  /** @type {number} Items per page for tank lists. */
  public noOfTanksItemsPerPage: number = 15;
  /*Tanks pagination end*/

  /*section pagination*/
  /** @type {number} Current page number for section lists. */
  public sectionCurrentPageNumber: number = 1;

  /** @type {number} Items per page for section lists. */
  public sectionNoOfItemsPerPage: number = 15;
  /*section pagination end*/

  /*Species pagination*/
  /** @type {number} Current page number for species lists. */
  public currentSpeciesPageNumber: number = 1;

  /** @type {number} Items per page for species lists. */
  public noOfSpeciesItemsPerPage: number = 15;
  /*Species pagination end*/

  /*totalsites pagination*/
  /** @type {number} Current page number for total sites lists. */
  public currentTotalSitesPageNumber: number = 1;

  /** @type {number} Items per page for total sites lists. */
  public noOfTotalSitesItemsPerPage: number = 15;
  /*totalsites pagination end*/

  /*section pagination end*/
  /** @type {any} Application permissions object. */
  public appPermission: any;

  /**
   * Constructor for BlubayDashboardComponent.
   * Initializes all required services.
   *
   * @param {ToastrService} toastService - Service for toast notifications.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {PondCultureService} pondCultureService - Service for pond culture operations.
   * @param {ReadingsService} readingsService - Service for readings operations.
   * @param {SpeciesTypeService} speciesTypeService - Service for species type operations.
   * @param {PondSectionService} pondSectionService - Service for pond section operations.
   * @param {CustomerDeviceService} customerDeviceService - Service for customer device operations.
   * @param {ServiceRequestService} serviceRequestService - Service for service request operations.
   * @param {PersonsService} personsService - Service for person operations.
   * @param {FeedLogService} feedLogService - Service for feed log operations.
   * @param {TreatmentLogService} treatmentLogService - Service for treatment log operations.
   * @param {SamplingService} samplingService - Service for sampling operations.
   * @param {HarvestLogService} harvestLogService - Service for harvest log operations.
   * @param {ObservationLogService} observationLogService - Service for observation log operations.
   * @param {ImageDetailsService} imageDetailsService - Service for image details operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {DomSanitizer} sanitizer - Service for DOM sanitization.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {Router} router - Angular Router.
   * @param {ToastrService} toastr - Toastr Service (duplicate injection).
   */
  constructor(private toastService: ToastrService, public businessEntityService: BusinessEntityService, public farmsiteEntityService: FarmSiteService,
              public pondCultureService: PondCultureService, public readingsService: ReadingsService, public speciesTypeService: SpeciesTypeService,
              public pondSectionService: PondSectionService,
              public personsService: PersonsService, public feedLogService: FeedLogService, public treatmentLogService: TreatmentLogService,
              public samplingService: SamplingService, public harvestLogService: HarvestLogService, public observationLogService: ObservationLogService,
              public imageDetailsService: ImageDetailsService, public customerPondService: CustomerPondService, public sidenavaction: Sidenavaction, public sanitizer: DomSanitizer, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, private router: Router, private toastr: ToastrService) {
    super();
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Retrieves user context from `localStorage`.
   * - Determines logic flow based on `loginPersonType` and `loginBusinessCategory`.
   * - Fetches comprehensive dashboard data using `Promise.all` for parallel execution.
   * - Calculates aggregate metrics (active ponds, stocking count, area, etc.).
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      let farmsiteIds = new Array();
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
      let tempPerm = Roles.find((item) => item.roleName == this.loginPersonType && item.businessCategory == this.loginBusinessCategory);
      this.appPermission = tempPerm.value.find((item) => item.component == 'BluBayDashboardComponent').permission;
      /* user login details */
      if (localStorage.length > 0) {
        this.businessId = localStorage.businessID;
        this.personId = localStorage.personID;
      }
      /* user login details end */
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      // get person list based on login businessid
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginBusinessCategory == 'Farm') {
        let farmSitesByBusinessId = await this.farmsiteEntityService.getFarmeSitesByFarmsId(this.businessId);
        farmsiteIds = farmSitesByBusinessId.map(farmsite => farmsite.id);
        let promiseResults = new Array();
        await Promise.all([
          await this.feedLogService.getAlltodayFeed(farmsiteIds), //getAlltodayFeed(onLoad)
          await this.treatmentLogService.getAlltodayTreatments(farmsiteIds), // getAlltodayTreatments(onLoad)
          await this.samplingService.getAlltodaySampling(farmsiteIds),  //getAlltodaySampling(onLoad)
          await this.harvestLogService.getAlltodayHarvest(farmsiteIds), //getAlltodayHarvest(onLoad)
          await this.observationLogService.getAlltodayObservation(farmsiteIds), //getAlltodayObservations(onLoad)
          await this.customerPondService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayTanksCount(onLoad)
          await this.customerPondService.getActivePondsCount(farmsiteIds), // getAllTotalPondsCount(onLoad)
          await this.pondCultureService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayCulturesCount(onLoad)
          await this.pondSectionService.getSectionCount(farmsiteIds), // getAllSectionCount(onLoad)
          await this.customerPondService.getInactivePondsCount(farmsiteIds), // getAllinActivePondsCount(onLoad)
          await this.readingsService.getReadingsByFarmSite(farmsiteIds), // getAlltodatTestsList(onLoad)
          await this.speciesTypeService.getSpeciesCount(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.speciesTypeService.getSpeciesTypeByFarmSites(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.businessEntityService.getCurrentDateFarmProviders(), // getAlltodayProvidersByAdmin(onLoad)
          await this.personsService.getAllPersonsFarmSitesBusinessByBusinessId(this.businessId), //getAllUsersCount
          await this.feedLogService.getSiteSummary(farmsiteIds), // getAllFarmSitesList
          await this.farmsiteEntityService.getCurrentDayFarmSitesByUserLogin(this.businessId), //getAlltodayFarmByUserLogin // Getting u dont have enough permission
          await this.imageDetailsService.getImageDetails(farmsiteIds)
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toastService.error(reject);
        });
        this.feedLogs = promiseResults[0];
        this.treatmentLogs = promiseResults[1];
        this.samplings = promiseResults[2];
        this.harvestLogs = promiseResults[3];
        this.observationLogs = promiseResults[4];
        this.currentDayCustomerPonds = promiseResults[5];
        this.activeCustomerPonds = promiseResults[6];
        this.pondCultures = promiseResults[7];
        this.pondSections = promiseResults[8];
        this.originalPondSections = this.pondSections;
        this.inActiveCustomerPonds = promiseResults[9];
        this.readings = promiseResults[10];
        this.speciesType = promiseResults[11];
        this.speciesTypes = promiseResults[12];
        this.providerBusinessEntities = promiseResults[13];
        this.persons = promiseResults[14];
        this.businessEntity = JSON.parse(localStorage.getItem('businessEntityById'));
        this.feedLogsWithFarmSite = promiseResults[15];
        this.originalFeedLogsWithFarmSite = this.feedLogsWithFarmSite;
        this.currentDayFarmSites = promiseResults[16];
        let images = promiseResults[17];
        this.farmSites = farmSitesByBusinessId;
        this.farmsCount = 0;
        this.waterQualityTest = 0;
        this.activePondCount = 0;
        this.activeStockingCount = 0;
        this.activeArea = 0;
        this.inActivePondsCount = 0;
        this.inActiveStockingCount = 0;
        this.inActiveArea = 0;
        this.imageCount = 0;
        if (this.businessEntity && this.businessEntity.businessType) {
          this.farmsCount = this.businessEntity.businessType.length;
        }
        this.readings.map(reading => {
          this.waterQualityTest += Number(reading.Nooftests); // test key
        });
        for (let a = 0; a < this.activeCustomerPonds.length; a++) {
          this.activePondCount += Number(this.activeCustomerPonds[a].totalPonds); // pond key
          this.activeStockingCount += Number(this.activeCustomerPonds[a].totalStockingCount); // stocking key
          this.activeArea += Number(this.activeCustomerPonds[a].totalPondSize); // area key
        }
        for (let b = 0; b < this.inActiveCustomerPonds.length; b++) {
          this.inActivePondsCount += Number(this.inActiveCustomerPonds[b].inactivePonds); // pond key
          this.inActiveStockingCount += Number(this.inActiveCustomerPonds[b].inactiveStockingCount); // stocking key
          this.inActiveArea += Number(this.inActiveCustomerPonds[b].inactivePondSize); // area key
        }
        for (let c = 0; c < images.length; c++) {
          this.imageCount += images[c].count;
        }
        for (let s = 0; s < this.samplings.length; s++) {
          if (this.samplings[s].abw) {
            this.samplings[s].abw = Number(this.samplings[s].abw.toString().match(/^\d+(?:\.\d{0,4})?/));
          }
          if (this.samplings[s].total_biomass) {
            this.samplings[s].total_biomass = Number(this.samplings[s].total_biomass.toString().match(/^\d+(?:\.\d{0,4})?/));
          }
        }
      }
      // get person list based on login businessid
      else if (this.loginPersonType == 'Farm Manager' && this.loginBusinessCategory == 'Farm') {
        let farmSitesByBusinessId = await this.farmsiteEntityService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
        farmsiteIds = farmSitesByBusinessId.map(farmsite => farmsite.id);
        let promiseResults = new Array();
        await Promise.all([
          await this.feedLogService.getAlltodayFeed(farmsiteIds), //getAlltodayFeed(onLoad)
          await this.treatmentLogService.getAlltodayTreatments(farmsiteIds), // getAlltodayTreatments(onLoad)
          await this.samplingService.getAlltodaySampling(farmsiteIds),  //getAlltodaySampling(onLoad)
          await this.harvestLogService.getAlltodayHarvest(farmsiteIds), //getAlltodayHarvest(onLoad)
          await this.observationLogService.getAlltodayObservation(farmsiteIds), //getAlltodayObservations(onLoad)
          await this.customerPondService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayTanksCount(onLoad)
          await this.customerPondService.getActivePondsCount(farmsiteIds), // getAllTotalPondsCount(onLoad)
          await this.pondCultureService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayCulturesCount(onLoad)
          await this.pondSectionService.getSectionCount(farmsiteIds), // getAllSectionCount(onLoad)
          await this.customerPondService.getInactivePondsCount(farmsiteIds), // getAllinActivePondsCount(onLoad)
          await this.readingsService.getReadingsByFarmSite(farmsiteIds), // getAlltodatTestsList(onLoad)
          await this.speciesTypeService.getSpeciesCount(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.speciesTypeService.getSpeciesTypeByFarmSites(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.businessEntityService.getCurrentDateFarmProviders(), // getAlltodayProvidersByAdmin(onLoad)
          await this.personsService.getAllPersonsFarmSitesBusinessByBusinessId(this.businessId), //getAllUsersCount
          await this.feedLogService.getSiteSummary(farmsiteIds), // getAllFarmSitesList
          await this.farmsiteEntityService.getCurrentDayFarmSitesByUserLogin(this.businessId), //getAlltodayFarmByUserLogin // Getting u dont have enough permission
          await this.imageDetailsService.getImageDetails(farmsiteIds)
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toastService.error(reject);
        });
        this.feedLogs = promiseResults[0];
        this.treatmentLogs = promiseResults[1];
        this.samplings = promiseResults[2];
        this.harvestLogs = promiseResults[3];
        this.observationLogs = promiseResults[4];
        this.currentDayCustomerPonds = promiseResults[5];
        this.activeCustomerPonds = promiseResults[6];
        this.pondCultures = promiseResults[7];
        this.pondSections = promiseResults[8];
        this.originalPondSections = this.pondSections;
        this.inActiveCustomerPonds = promiseResults[9];
        this.readings = promiseResults[10];
        this.speciesType = promiseResults[11];
        this.speciesTypes = promiseResults[12];
        this.providerBusinessEntities = promiseResults[13];
        this.persons = promiseResults[14];
        this.businessEntity = JSON.parse(localStorage.getItem('businessEntityById'));
        this.feedLogsWithFarmSite = promiseResults[15];
        this.originalFeedLogsWithFarmSite = this.feedLogsWithFarmSite;
        this.currentDayFarmSites = promiseResults[16];
        let images = promiseResults[17];
        this.farmSites = farmSitesByBusinessId;
        this.farmsCount = 0;
        this.waterQualityTest = 0;
        this.activePondCount = 0;
        this.activeStockingCount = 0;
        this.activeArea = 0;
        this.inActivePondsCount = 0;
        this.inActiveStockingCount = 0;
        this.inActiveArea = 0;
        this.imageCount = 0;
        if (this.businessEntity && this.businessEntity.businessType) {
          this.farmsCount = this.businessEntity.businessType.length;
        }
        this.readings.map(reading => {
          this.waterQualityTest += Number(reading.Nooftests); // test key
        });
        for (let a = 0; a < this.activeCustomerPonds.length; a++) {
          this.activePondCount += Number(this.activeCustomerPonds[a].totalPonds); // pond key
          this.activeStockingCount += Number(this.activeCustomerPonds[a].totalStockingCount); // stocking key
          this.activeArea += Number(this.activeCustomerPonds[a].totalPondSize); // area key
        }
        for (let b = 0; b < this.inActiveCustomerPonds.length; b++) {
          this.inActivePondsCount += Number(this.inActiveCustomerPonds[b].inactivePonds); // pond key
          this.inActiveStockingCount += Number(this.inActiveCustomerPonds[b].inactiveStockingCount); // stocking key
          this.inActiveArea += Number(this.inActiveCustomerPonds[b].inactivePondSize); // area key
        }
        for (let c = 0; c < images.length; c++) {
          this.imageCount += images[c].count;
        }
      } else if (this.loginPersonType == 'Business Admin' && this.loginBusinessCategory == 'Provider') {
        let farmsitesByProviderId = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(this.businessId);
        // let farmsitesByProviderId = localStorage.farmsitesByProviderId;
        farmsiteIds = farmsitesByProviderId.map(farmsite => farmsite.id);
        let promiseResults = new Array();
        await Promise.all([
          await this.feedLogService.getAlltodayFeed(farmsiteIds), //getAlltodayFeed(onLoad)
          await this.treatmentLogService.getAlltodayTreatments(farmsiteIds), // getAlltodayTreatments(onLoad)
          await this.samplingService.getAlltodaySampling(farmsiteIds),  //getAlltodaySampling(onLoad)
          await this.harvestLogService.getAlltodayHarvest(farmsiteIds), //getAlltodayHarvest(onLoad)
          await this.observationLogService.getAlltodayObservation(farmsiteIds), //getAlltodayObservations(onLoad)
          await this.customerPondService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayTanksCount(onLoad)
          await this.customerPondService.getActivePondsCount(farmsiteIds), // getAllTotalPondsCount(onLoad)
          await this.pondCultureService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayCulturesCount(onLoad)
          await this.pondSectionService.getSectionCount(farmsiteIds), // getAllSectionCount(onLoad)
          await this.customerPondService.getInactivePondsCount(farmsiteIds), // getAllinActivePondsCount(onLoad)
          await this.readingsService.getReadingsByFarmSite(farmsiteIds), // getAlltodatTestsList(onLoad)
          await this.speciesTypeService.getSpeciesCount(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.speciesTypeService.getSpeciesTypeByFarmSites(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.businessEntityService.getCurrentDateFarmProviders(), // getAlltodayProvidersByAdmin(onLoad)
          await this.personsService.getAllPersonsFarmSitesBusinessByBusinessId(this.businessId), //getAllUsersCount
          await this.businessEntityService.getAllFarmCounts(), //getAllFarmsCount
          await this.farmsiteEntityService.getCurrentDayFarmSitesByUserLogin(this.businessId), //getAlltodayFarmByUserLogin
          await this.businessEntityService.getCurrentDateFarmProvidersByproviderlogin(), // getAlltodayProvidersByProvider
          await this.feedLogService.getSiteSummary(farmsiteIds), // getAllFarmSitesList(onLoad)
          await this.imageDetailsService.getImageDetails(farmsiteIds)
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toastService.error(reject);
        });
        this.feedLogs = promiseResults[0];
        this.treatmentLogs = promiseResults[1];
        this.samplings = promiseResults[2];
        this.harvestLogs = promiseResults[3];
        this.observationLogs = promiseResults[4];
        this.currentDayCustomerPonds = promiseResults[5];
        this.activeCustomerPonds = promiseResults[6];
        this.pondCultures = promiseResults[7];
        this.pondSections = promiseResults[8];
        this.originalPondSections = this.pondSections;
        this.inActiveCustomerPonds = promiseResults[9];
        this.readings = promiseResults[10];
        this.speciesType = promiseResults[11];
        this.speciesTypes = promiseResults[12];
        this.providerBusinessEntities = promiseResults[13]; // onload
        this.persons = promiseResults[14];
        this.businessEntities = promiseResults[15];
        this.currentDayFarmSites = promiseResults[16];
        this.providerBusinessEntities = promiseResults[17];
        this.feedLogsWithFarmSite = promiseResults[18];
        this.originalFeedLogsWithFarmSite = this.feedLogsWithFarmSite;
        let images = promiseResults[19];
        this.waterQualityTest = 0;
        this.activePondCount = 0;
        this.activeStockingCount = 0;
        this.activeArea = 0;
        this.inActivePondsCount = 0;
        this.inActiveStockingCount = 0;
        this.inActiveArea = 0;
        this.imageCount = 0;
        this.readings.map(reading => {
          this.waterQualityTest += Number(reading.Nooftests); // test key
        });
        for (let a = 0; a < this.activeCustomerPonds.length; a++) {
          this.activePondCount += Number(this.activeCustomerPonds[a].totalPonds); // pond key
          this.activeStockingCount += Number(this.activeCustomerPonds[a].totalStockingCount); // stocking key
          this.activeArea += Number(this.activeCustomerPonds[a].totalPondSize); // area key
        }
        for (let b = 0; b < this.inActiveCustomerPonds.length; b++) {
          this.inActivePondsCount += Number(this.inActiveCustomerPonds[b].inactivePonds); // pond key
          this.inActiveStockingCount += Number(this.inActiveCustomerPonds[b].inactiveStockingCount); // stocking key
          this.inActiveArea += Number(this.inActiveCustomerPonds[b].inactivePondSize); // area key
        }
        for (let c = 0; c < images.length; c++) {
          this.imageCount += images[c].count;
        }
      } else {
        let farmSites = await this.farmsiteEntityService.getAllFarmSites();
        farmsiteIds = farmSites.map(farmsite => farmsite.id);
        let promiseResults = new Array();
        await Promise.all([
          await this.feedLogService.getAlltodayFeed(farmsiteIds), //getAlltodayFeed(onLoad)
          await this.feedLogService.getSiteSummary(farmsiteIds), // getAllFarmSitesList(onLoad)
          await this.treatmentLogService.getAlltodayTreatments(farmsiteIds), // getAlltodayTreatments(onLoad)
          await this.samplingService.getAlltodaySampling(farmsiteIds),  //getAlltodaySampling(onLoad)
          await this.harvestLogService.getAlltodayHarvest(farmsiteIds), //getAlltodayHarvest(onLoad)
          await this.observationLogService.getAlltodayObservation(farmsiteIds), //getAlltodayObservations(onLoad)
          await this.customerPondService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayTanksCount(onLoad)
          await this.customerPondService.getActivePondsCount(farmsiteIds), // getAllTotalPondsCount(onLoad)
          await this.pondCultureService.getCurrentDatePondsByFarmSiteId(farmsiteIds), // getAlltodayCulturesCount(onLoad)
          await this.pondSectionService.getSectionCount(farmsiteIds), // getAllSectionCount(onLoad)
          await this.customerPondService.getInactivePondsCount(farmsiteIds), // getAllinActivePondsCount(onLoad)
          await this.readingsService.getReadingsByFarmSite(farmsiteIds), // getAlltodatTestsList(onLoad)
          await this.speciesTypeService.getSpeciesCount(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.speciesTypeService.getSpeciesTypeByFarmSites(farmsiteIds), // getAllSpeciesCount(onLoad)
          await this.businessEntityService.getAllBusiness(), //getAllFarmsCountByAdmin
          await this.businessEntityService.getCurrentDateFarmProviders(), // getAlltodayProvidersByAdmin(onLoad)
          await this.personsService.getAllPersonsFarmSitesBusinessByBusinessId(this.businessId), //getAllUsersCount
          await this.farmsiteEntityService.getCurrentDayFarmSites(), // getAlltodayFarmByAdmin
          await this.imageDetailsService.getImageDetails(farmsiteIds)
        ]).then((resolve) => {
          promiseResults = resolve;
        }).catch((reject) => {
          this.toastService.error(reject);
        });
        this.feedLogs = promiseResults[0];
        this.feedLogsWithFarmSite = promiseResults[1];
        this.originalFeedLogsWithFarmSite = this.feedLogsWithFarmSite;
        this.treatmentLogs = promiseResults[2];
        this.samplings = promiseResults[3];
        this.harvestLogs = promiseResults[4];
        this.observationLogs = promiseResults[5];
        this.currentDayCustomerPonds = promiseResults[6];
        this.activeCustomerPonds = promiseResults[7];
        this.pondCultures = promiseResults[8];
        this.pondSections = promiseResults[9];
        this.originalPondSections = this.pondSections;
        this.inActiveCustomerPonds = promiseResults[10];
        this.readings = promiseResults[11];
        this.speciesType = promiseResults[12];
        this.speciesTypes = promiseResults[13];
        this.farmSites = farmSites;
        this.businessEntities = promiseResults[14];
        this.providerBusinessEntities = promiseResults[15];
        this.persons = promiseResults[16];
        this.currentDayFarmSites = promiseResults[17];
        let images = promiseResults[18];
        this.farmsCount = 0;
        this.waterQualityTest = 0;
        this.activePondCount = 0;
        this.activeStockingCount = 0;
        this.activeArea = 0;
        this.inActivePondsCount = 0;
        this.inActiveStockingCount = 0;
        this.inActiveArea = 0;
        this.imageCount = 0;
        this.businessEntities.map(businessEntity => {
        if (businessEntity && businessEntity.businessType[0] && businessEntity.businessType[0].businessTypeID == '618916f2b4391c6d48137e3d') {
            this.farmsCount += 1;
          }
        });
        this.readings.map(reading => {
          this.waterQualityTest += Number(reading.Nooftests); // test key
        });
        for (let a = 0; a < this.activeCustomerPonds.length; a++) {
          this.activePondCount += Number(this.activeCustomerPonds[a].totalPonds); // pond key
          this.activeStockingCount += Number(this.activeCustomerPonds[a].totalStockingCount); // stocking key
          this.activeArea += Number(this.activeCustomerPonds[a].totalPondSize); // area key
        }
        for (let b = 0; b < this.inActiveCustomerPonds.length; b++) {
          this.inActivePondsCount += Number(this.inActiveCustomerPonds[b].inactivePonds); // pond key
          this.inActiveStockingCount += Number(this.inActiveCustomerPonds[b].inactiveStockingCount); // stocking key
          this.inActiveArea += Number(this.inActiveCustomerPonds[b].inactivePondSize); // area key
        }
        for (let c = 0; c < images.length; c++) {
          this.imageCount += images[c].count;
        }
      }
      this.responseLoader = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks and state issues.
   * @side-effect Cancels HTTP requests for all injected services.
   */
  //To cancel ongoing requests and unsubscribe from subscriptions.
  public ngOnDestroy(): void {
    this.feedLogService.cancelOnGoingHttpRequests();
    this.treatmentLogService.cancelOnGoingHttpRequests();
    this.samplingService.cancelOnGoingHttpRequests();
    this.harvestLogService.cancelOnGoingHttpRequests();
    this.observationLogService.cancelOnGoingHttpRequests();
    this.imageDetailsService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.pondCultureService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
    this.readingsService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.speciesTypeService.cancelOnGoingHttpRequests();
    this.farmsiteEntityService.cancelOnGoingHttpRequests();
    this.personsService.cancelOnGoingHttpRequests();
  }

  /* Image doenload */
  /**
   * @method getAllImageDownload
   * @async
   * @description Downloads images associated with a specific list item (e.g., check tray).
   * @param {any} list - The list item containing IDs for image retrieval.
   */
  public async getAllImageDownload(list: any) {
    try {
      let imageDetails = new ImageDetailsModel();
      imageDetails.farm_id = this.businessId;
      imageDetails.site_id = list.farm_site_id + '';
      imageDetails.section_id = list.sectionid + '';
      imageDetails.pond_id = list.pondid + '';
      imageDetails.function = 'cti';
      imageDetails.pond_culture_id = list.pondcultureid + '';
      imageDetails.check_tray_id = list.checktrayid + '';
      this.imagesList = await this.imageDetailsService.imageDownload(imageDetails);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /* End image download */

  // Call this method in the image source, it will sanitize it.
  /**
   * @method transform
   * @description Sanitizes a base64 image string for safe use in the DOM.
   * @param {any} base64Image - The base64 image string.
   * @returns {SafeResourceUrl} The sanitized resource URL.
   */
  transform(base64Image: any) {
    return this.sanitizer.bypassSecurityTrustResourceUrl('data:image/png;base64, ' + base64Image);
  }

  /**
   * @method imageHideShow
   * @description Shows the image modal for a selected image object.
   * @param {any} selectedImageObj - The image object to display.
   */
  imageHideShow(selectedImageObj) {
    this.selectedImageObj = selectedImageObj;
    this.imageshow = true;
  }

  /**
   * @method closeModel
   * @description Closes the image modal and clears the selected image object.
   */
  closeModel() {
    this.selectedImageObj = null;
  }

  /**
   * @method onImagesClick
   * @description Navigates to the detailed images view if images are available.
   * Shows a warning toast if no images are available.
   */
  onImagesClick(): void {
    this.toastr.clear();
    if (this.imageCount && this.imageCount > 0) {
      this.router.navigate(['/home/imagesDetails']);
    } else {
      this.toastr.warning('No Images available to view', 'Info');
    }
  }
}
